import {z} from "zod/v4-mini"
import {mapFields} from "@cm/utils"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {GeomBuilderContext} from "#template/geometry-processing/geometry-graph"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {EvaluableGeometryGeneratorArgs, GeometryGenerator, GeometryGeneratorArg} from "#template/nodes/geometry-graph/lambda-types"
import {
    evaluableGeometryGeneratorArgs,
    generateGeometryLambdaRuntimeNode,
    geometryGenerator,
    getHashForGeometryGeneratorArgs,
} from "#template/nodes/geometry-graph/lambda-types"

export type AttributeSetLambdaParameters<T extends IndexingDim> = {
    name: string
    args: EvaluableGeometryGeneratorArgs | null
    generator: GeometryGenerator<GeometryAttributeSetData<T>> | null
}

export const attributeSetLambdaParametersSchema = <T extends IndexingDim>() =>
    z.object({
        name: z.string(),
        args: z.nullable(evaluableGeometryGeneratorArgs),
        generator: z.nullable(geometryGenerator<GeometryAttributeSetData<T>>()),
    })

export abstract class GeometryAttributeSetLambdaNode<
    out T extends IndexingDim,
    out P extends AttributeSetLambdaParameters<T> = AttributeSetLambdaParameters<T>,
> extends GeometryAttributeSetNode<T, P> {
    protected override hasValidParameters(parameters: P): parameters is P & NonNullableParameters<P> {
        return parameters.args !== null && parameters.generator !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<P>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
        ctx: GeomBuilderContext,
        sceneManager: ISceneManager,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {args, generator} = parameters

        const evaluatedArgs = scope.record<string, GeometryGeneratorArg>(
            mapFields(args, (value) => evaluator.evaluate(value) as BuilderInlet<GeometryGeneratorArg>),
        )
        const evaluatedArgsWithHash = scope.pureLambda(
            evaluatedArgs,
            (evaluatedArgs) => ({
                args: evaluatedArgs,
                hash: getHashForGeometryGeneratorArgs(evaluatedArgs),
            }),
            "GeometryGeneratorArgsWithHash",
        )

        const lambdaRuntimeNodeClass = generateGeometryLambdaRuntimeNode<GeometryAttributeSetData<T>>(this.nodeClass)

        const {output} = scope.node(lambdaRuntimeNodeClass, {
            sceneManager,
            geomBuilderContext: ctx,
            argsWithHash: evaluatedArgsWithHash,
            generator,
        })

        const geometryAttributeSetData = scope.get(output, "output")
        return {evaluatedGeometryData: geometryAttributeSetData}
    }
}
